import type { MouseEvent } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Button,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import type { MoldSelectorProps, StrandCardData } from "../types";

/**
 * MoldSelector — strand-based selector for the Caster Mold map view.
 *
 * Always renders exactly 5 cards (one per strand). Each card either
 * shows the installed mold info (name, component count, uninstall toggle)
 * or an "Empty" placeholder with an "Install" button that opens the
 * SetMoldOnlineModal in "fromStrand" mode.
 */
export const MoldSelector = ({
  strands,
  selectedStrandId,
  onSelectStrand,
  onSetOffline,
  onInstallMoldToStrand,
}: MoldSelectorProps) => {
  const handleUninstallClick = async (
    event: MouseEvent<HTMLButtonElement>,
    moldId: number,
  ) => {
    event.stopPropagation();
    await onSetOffline(moldId);
  };

  const handleInstallClick = (
    event: MouseEvent<HTMLButtonElement>,
    strandId: number,
  ) => {
    event.stopPropagation();
    onInstallMoldToStrand(strandId);
  };

  const renderStrandCard = (strand: StrandCardData) => {
    const { mold } = strand;

    if (mold) {
      return renderOccupiedCard(strand);
    }

    return renderEmptyCard(strand);
  };

  /** Card for a strand that has a mold installed */
  const renderOccupiedCard = (strand: StrandCardData) => {
    const { mold } = strand;
    if (!mold) return null;

    const isSelected = selectedStrandId === strand.strandId;
    const componentCount = mold.locations.filter(
      (loc) => loc.rowType === "location" && !loc.HasChildren,
    ).length;

    return (
      <Paper
        key={strand.strandId}
        onClick={() => onSelectStrand(strand.strandId)}
        elevation={isSelected ? 3 : 1}
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: theme.spacing(1, 2),
          cursor: "pointer",
          minWidth: 130,
          flex: 1,
          transition: "border-color 0.2s ease, background-color 0.2s ease",
          border: isSelected
            ? `2px solid ${theme.palette.primary.main}`
            : "2px solid transparent",
          backgroundColor: isSelected
            ? theme.palette.action.selected
            : "transparent",
          overflow: "visible",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        })}
      >
        {/* Strand label */}
        <Typography
          sx={(theme) => ({
            fontSize: "0.7rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: theme.palette.text.secondary,
            mb: 0.5,
          })}
        >
          {strand.strandName}
        </Typography>

        {/* Mold schematic icon */}
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: 90,
            height: 90,
            mb: 0.25,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.success.main, 0.08),
          })}
        >
          <ViewInArIcon
            sx={(theme) => ({
              fontSize: 40,
              color: theme.palette.success.main,
            })}
          />
          <Chip
            label={`${componentCount} parts`}
            size="small"
            sx={(theme) => ({
              mt: 0.5,
              height: 20,
              fontSize: "0.65rem",
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.text.secondary,
            })}
          />
        </Box>

        <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 0 }}>
          {mold.moldName}
        </Typography>

        <Tooltip title="Set Offline">
          <IconButton
            onClick={(e) => handleUninstallClick(e, mold.moldId)}
            sx={(theme) => ({
              padding: theme.spacing(0.5),
              marginTop: theme.spacing(0.5),
              color: theme.palette.success.main,
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
              },
            })}
          >
            <PowerSettingsNewIcon />
          </IconButton>
        </Tooltip>
      </Paper>
    );
  };

  /** Card for an empty strand (no mold installed) */
  const renderEmptyCard = (strand: StrandCardData) => {
    const isSelected = selectedStrandId === strand.strandId;

    return (
      <Paper
        key={strand.strandId}
        elevation={isSelected ? 3 : 0}
        onClick={() => onSelectStrand(strand.strandId)}
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: theme.spacing(1, 2),
          cursor: "pointer",
          minWidth: 130,
          flex: 1,
          transition: "border-color 0.2s ease, background-color 0.2s ease",
          border: isSelected
            ? `2px solid ${theme.palette.primary.main}`
            : `2px dashed ${theme.palette.divider}`,
          backgroundColor: isSelected
            ? theme.palette.action.selected
            : theme.palette.action.hover,
          gap: 1,
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        })}
      >
        {/* Strand label */}
        <Typography
          sx={(theme) => ({
            fontSize: "0.7rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: theme.palette.text.secondary,
          })}
        >
          {strand.strandName}
        </Typography>

        <Typography
          sx={(theme) => ({
            fontSize: "0.85rem",
            fontWeight: 500,
            color: theme.palette.text.disabled,
          })}
        >
          Empty
        </Typography>

        <Button
          variant="outlined"
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          onClick={(e) => handleInstallClick(e, strand.strandId)}
          sx={{ textTransform: "none", fontSize: "0.75rem" }}
        >
          Install
        </Button>
      </Paper>
    );
  };

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        padding: theme.spacing(1),
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
      })}
    >
      {/* Section header */}
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "text.secondary",
          textAlign: "center",
          py: 0.5,
        }}
      >
        Strands
      </Typography>

      {/* Strand cards row */}
      <Box sx={(theme) => ({ display: "flex", gap: theme.spacing(1) })}>
        {strands.map(renderStrandCard)}
      </Box>
    </Box>
  );
};
